import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

interface SitemapState {
    urlsInput: string;
    changefreq: string;
    priority: string;
    includeLastmod: boolean;
    copied: boolean;
}

const CHANGEFREQ_VALUES = ['', 'always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'];

export default class SitemapGeneratorComponent extends Component<{ feature?: string }, SitemapState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: SitemapState = {
        urlsInput: '',
        changefreq: 'weekly',
        priority: '0.5',
        includeLastmod: true,
        copied: false,
    };

    private escapeXml(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
    }

    private isAbsoluteUrl(s: string): boolean {
        // Accept http(s) URLs only; reject relative paths and other schemes
        // so we never emit a `<loc>` value that fails sitemaps.org validation.
        try {
            const u = new URL(s);
            return u.protocol === 'http:' || u.protocol === 'https:';
        } catch {
            return false;
        }
    }

    private parseUrls(): { valid: string[]; invalid: string[] } {
        const all = this.state.urlsInput
            .split('\n')
            .map(l => l.trim())
            .filter(l => l.length > 0 && !l.startsWith('#'));
        const valid: string[] = [];
        const invalid: string[] = [];
        for (const l of all) (this.isAbsoluteUrl(l) ? valid : invalid).push(l);
        return { valid, invalid };
    }

    private generate(): string {
        const { valid: urls } = this.parseUrls();
        const { changefreq, priority, includeLastmod } = this.state;
        const today = new Date().toISOString().split('T')[0];

        if (urls.length === 0) {
            return '<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n</urlset>';
        }

        const entries = urls.map(url => {
            const parts: string[] = [`  <url>`, `    <loc>${this.escapeXml(url)}</loc>`];
            if (includeLastmod) parts.push(`    <lastmod>${today}</lastmod>`);
            if (changefreq) parts.push(`    <changefreq>${changefreq}</changefreq>`);
            if (priority) parts.push(`    <priority>${priority}</priority>`);
            parts.push(`  </url>`);
            return parts.join('\n');
        }).join('\n');

        return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${entries}\n</urlset>`;
    }

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    downloadOutput = () => {
        const blob = new Blob([this.generate()], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sitemap.xml';
        a.click();
        URL.revokeObjectURL(url);
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { urlsInput, changefreq, priority, includeLastmod, copied } = this.state;
        const parsed = this.parseUrls();
        const urlCount = parsed.valid.length;
        const invalidCount = parsed.invalid.length;
        const output = this.generate();

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}>
                        <span className={genStyles.sectionTitle}>URLs</span>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="urls">URLs (one per line)</label>
                        <textarea id="urls" rows={10} value={urlsInput}
                            placeholder={'https://example.com/\nhttps://example.com/about\nhttps://example.com/contact'}
                            onChange={e => this.setState({ urlsInput: e.target.value })} />
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{urlCount} URL{urlCount === 1 ? '' : 's'} parsed</span>
                        {invalidCount > 0 && (
                            <span style={{ fontSize: '0.72rem', color: '#b45309' }}>
                                {invalidCount} line{invalidCount === 1 ? '' : 's'} skipped — sitemaps.org requires absolute http(s) URLs.
                            </span>
                        )}
                    </div>
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}>
                        <span className={genStyles.sectionTitle}>Per-URL options</span>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="changefreq">changefreq</label>
                            <select id="changefreq" value={changefreq} onChange={e => this.setState({ changefreq: e.target.value })}>
                                {CHANGEFREQ_VALUES.map(v => <option key={v} value={v}>{v || '(omit)'}</option>)}
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="priority">priority (0.0–1.0)</label>
                            <input id="priority" type="number" inputMode="decimal" min={0} max={1} step={0.1} value={priority}
                                onChange={e => this.setState({ priority: e.target.value })} />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={includeLastmod}
                            onChange={e => this.setState({ includeLastmod: e.target.checked })} />
                        Include <code>lastmod</code> (today's date)
                    </label>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated sitemap.xml"
                previewMeta={`${urlCount} URL${urlCount === 1 ? '' : 's'}`}
                preview={<CodeBlock code={output} language="xml" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated sitemap XML" />}
                previewActions={<>
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={urlCount === 0}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`}
                        onClick={this.downloadOutput} disabled={urlCount === 0}>
                        ⬇ Download .xml
                    </button>
                </>}
            />
        );
    }
}
